import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Check } from 'lucide-react'
import type { CompanyPipelineDto } from '@lib/dto/pipeline.dto'
import { Card } from '@/components/ui/card'
import { formatCount } from '@/lib/adFormat'
import { connectableText, type CompanyConnections } from './pipelineText'

type Row = CompanyPipelineDto['rows'][number]

function Note({ tone, children }: { tone: 'ok' | 'attention'; children: ReactNode }) {
  return (
    <li className="flex gap-3 border-t border-line py-3 text-sm text-ink">
      {tone === 'ok' ? (
        <Check aria-label="Working" className="mt-0.5 h-4 w-4 shrink-0 text-ok-ink" />
      ) : (
        <AlertTriangle aria-label="Needs a look" className="mt-0.5 h-4 w-4 shrink-0 text-hold-ink" />
      )}
      <p className="m-0">{children}</p>
    </li>
  )
}

/** What goes before the `i`th of `n` names in a list read aloud: nothing, a comma, or "and". */
function separator(i: number, n: number): string {
  if (i === 0) return ''
  return i === n - 1 ? ' and ' : ', '
}

/** Venue names, each linked to what its own Pipeline says it can't see: "A, B and C". */
function Venues({ rows, linkTo }: { rows: readonly Row[]; linkTo: (venueId: string) => string }) {
  return rows.map((row, i) => (
    <Fragment key={row.venueId}>
      {separator(i, rows.length)}
      <Link to={`${linkTo(row.venueId)}#tracking`} className="font-medium text-accent-ink hover:underline">
        {row.venueName}
      </Link>
    </Fragment>
  ))
}

interface CompanyTrackingProps {
  rows: readonly Row[]
  linkTo: (venueId: string) => string
  /** What the company has switched on: the venues are said to lack only what they could connect. */
  companyHas: CompanyConnections
}

/**
 * Venues counting inquiries only from their events: not connected to what the
 * company has, or, at a company with neither connection, said as it is with
 * nothing to connect.
 */
function Unconnected({ rows, linkTo, companyHas }: CompanyTrackingProps) {
  const which = connectableText(companyHas)
  if (which === null) {
    return (
      <>
        At <Venues rows={rows} linkTo={linkTo} />, inquiries are counted only from the events made in FLOH Venues.
      </>
    )
  }
  return (
    <>
      <Venues rows={rows} linkTo={linkTo} /> {rows.length === 1 ? 'isn’t' : 'aren’t'} connected to {which}, so {rows.length === 1 ? 'its' : 'their'}{' '}
      inquiries are counted only from the events made in FLOH Venues.
    </>
  )
}

/**
 * What the company's numbers can't see (docs/pipeline-view.spec.md,
 * "Tracking"), venue by venue: which aren't connected, which have lead sources
 * nobody has said the meaning of, and the bookings with no lead. Each venue
 * links to its own Pipeline, where the fixes are.
 */
export function CompanyTracking({ rows, linkTo, companyHas }: CompanyTrackingProps) {
  const known = rows.filter((r) => r.tracking !== null)
  if (known.length === 0) return null
  const unconnected = known.filter((r) => r.tracking?.connections.length === 0)
  const unmapped = known.filter((r) => (r.tracking?.unmappedSources.length ?? 0) > 0)
  const unmappedInquiries = unmapped.reduce((a, r) => a + (r.tracking?.unmappedInquiries ?? 0), 0)
  const withoutLead = known.reduce((a, r) => a + (r.tracking?.bookingsWithoutLead ?? 0), 0)
  const fine = unconnected.length === 0 && unmapped.length === 0 && withoutLead === 0
  return (
    <Card asChild>
      <section id="tracking" aria-labelledby="company-tracking-heading" className="p-5">
        <h2 id="company-tracking-heading" className="m-0 text-heading font-semibold text-ink">
          Tracking across venues
        </h2>
        <p className="m-0 mb-2 text-small text-ink-muted">Why some numbers read Not tracked or Unknown</p>
        <ul className="m-0 list-none p-0">
          {unconnected.length === 0 ? null : (
            <Note tone="attention">
              <Unconnected rows={unconnected} linkTo={linkTo} companyHas={companyHas} />
            </Note>
          )}
          {unmapped.length === 0 ? null : (
            <Note tone="attention">
              At <Venues rows={unmapped} linkTo={linkTo} />, a lead source doesn’t say where couples came from yet, so {formatCount(unmappedInquiries)}{' '}
              {unmappedInquiries === 1 ? 'inquiry counts' : 'inquiries count'} as Unknown.
            </Note>
          )}
          {withoutLead === 0 ? null : (
            <Note tone="attention">
              {formatCount(withoutLead)} {withoutLead === 1 ? 'booking isn’t' : 'bookings aren’t'} joined to a lead, so where{' '}
              {withoutLead === 1 ? 'it' : 'they'} came from is Unknown.
            </Note>
          )}
          {fine ? <Note tone="ok">Every venue’s inquiries come in from a connection, and every lead source says where couples came from.</Note> : null}
        </ul>
      </section>
    </Card>
  )
}
